import React from 'react';
import clsx from 'clsx';
import { toast } from 'react-toastify';
import { getSheetData, getFileContent, mapFileContent } from '@utils';
import { ActionDialog, ErrorModal } from '@shared/components';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import BackupIcon from '@material-ui/icons/Backup';
import WarningIcon from '@material-ui/icons/Warning';
import useStyles from './style';

const noop = () => {};
const defaultState = {
  showUploadConfirm: false,
  selectedFile: null,
  errorModal: {},
};
const UploadSection = ({
  paperElevation = 4,
  sheetHeaders = {},
  phoneNumberList = [],
  extensionList = [],
  isSyncing = false,
  onUpload = noop,
}) => {
  const classes = useStyles();
  const [state, setState] = React.useState(defaultState);
  const validateFile = (sheetData = {}) => {
    let isFileCorrect = true;
    Object.keys(sheetHeaders.NAMING).map(key => {
      if (!(sheetData.naming[0] || []).includes(sheetHeaders.NAMING[key])) {
        isFileCorrect = false;
      }
    });
    Object.keys(sheetHeaders.ASSIGNING).map(key => {
      if (
        !(sheetData.assigning[0] || []).includes(sheetHeaders.ASSIGNING[key])
      ) {
        isFileCorrect = false;
      }
    });
    return isFileCorrect;
  };

  const handleFileChanges = event => {
    let selectedFile = null;
    if (event.currentTarget.files && event.currentTarget.files.length > 0) {
      selectedFile = event.currentTarget.files[0];
    }
    if (!event.currentTarget.files && event.currentTarget.files.length === 0) {
      toast.error('Selected file is empty');
      selectedFile = null;
    }
    setState(prevState => ({
      ...prevState,
      selectedFile,
    }));
  };

  const handleUpload = async selectedFile => {
    const fileContent = await getFileContent(selectedFile, { header: 1 });
    const sheetData = {
      naming: getSheetData(fileContent, fileContent.SheetNames[1]),
      assigning: getSheetData(fileContent, fileContent.SheetNames[2]),
    };

    const dataBeingUploaded = {
      naming: mapFileContent(sheetData.naming),
      assigning: mapFileContent(sheetData.assigning),
    };
    const isValidFile = validateFile(sheetData);
    if (
      (!dataBeingUploaded.assigning.length &&
        !dataBeingUploaded.naming.length) ||
      !fileContent ||
      Object.keys(fileContent).length === 0 ||
      !isValidFile
    ) {
      setState(prevState => ({
        ...prevState,
        errorModal: {
          show: true,
          errorMessage: 'Invalid file',
          errorBody: [
            !isValidFile ? 'File content is invalid' : 'Selected file is empty',
          ],
        },
      }));
      return;
    }
    let notAvailableNumbers = {};
    const modifiedAssigningData = dataBeingUploaded.assigning
      .filter((item, index) => {
        const desiredPhoneNumber =
          item[sheetHeaders.ASSIGNING.desiredPhoneNumber];
        const phoneNumberId = (
          phoneNumberList.find(
            item => item.phoneNumber === desiredPhoneNumber
          ) || {}
        ).id;
        if (desiredPhoneNumber && !phoneNumberId) {
          notAvailableNumbers = {
            ...notAvailableNumbers,
            [desiredPhoneNumber]: notAvailableNumbers[desiredPhoneNumber]
              ? [...notAvailableNumbers[desiredPhoneNumber], index + 2]
              : [index + 2],
          };
        }
        return !!desiredPhoneNumber;
      })
      .map(item => {
        let extensionId = item[sheetHeaders.ASSIGNING.extensionId];
        let extensionNumber = item[sheetHeaders.ASSIGNING.extensionNumber];
        const desiredPhoneNumber =
          item[sheetHeaders.ASSIGNING.desiredPhoneNumber];
        const phoneNumber =
          phoneNumberList.find(
            item => item.phoneNumber === desiredPhoneNumber
          ) || {};
        if (!extensionId || !extensionNumber) {
          const extension =
            extensionList.find(
              ext =>
                +ext.extensionNumber === +extensionNumber ||
                +ext.id === +extensionId
            ) || {};
          extensionId = extensionId || extension?.id;
          extensionNumber = extensionNumber || extension?.extensionNumber;
        }
        return {
          extensionNumber,
          extensionId,
          usageType: phoneNumber.usageType || '',
          phoneNumberId: phoneNumber.id || '',
          desiredPhoneNumber,
          type: 'assigning',
        };
      });

    const modifiedNamingData = dataBeingUploaded.naming
      .filter((item, index) => {
        const desiredPhoneNumber = item[sheetHeaders.NAMING.currentPhoneNumber];
        const phoneNumberId = (
          phoneNumberList.find(
            item => item.phoneNumber === desiredPhoneNumber
          ) || {}
        ).id;
        if (desiredPhoneNumber && !phoneNumberId) {
          notAvailableNumbers = {
            ...notAvailableNumbers,
            [desiredPhoneNumber]: notAvailableNumbers[desiredPhoneNumber]
              ? [...notAvailableNumbers[desiredPhoneNumber], index + 2]
              : [index + 2],
          };
        }
        return !!desiredPhoneNumber;
      })
      .map(item => {
        const desiredPhoneNumber = item[sheetHeaders.NAMING.currentPhoneNumber];
        const phoneNumber =
          phoneNumberList.find(
            item => item.phoneNumber === desiredPhoneNumber
          ) || {};
        return {
          phoneNumberId: phoneNumber.id || '',
          phoneNumberName: item[sheetHeaders.NAMING.phoneNumberName],
          desiredPhoneNumber,
          type: 'naming',
        };
      });

    if (!modifiedAssigningData.length && !dataBeingUploaded.naming.length) {
      setState(prevState => ({
        ...prevState,
        errorModal: {
          show: true,
          errorMessage: 'Number(s) to Add not found',
          errorBody: ['Number(s) to Add are not found in uploaded file.'],
        },
      }));
      return;
    }
    if (!!Object.keys(notAvailableNumbers).length) {
      setState(prevState => ({
        ...prevState,
        errorModal: {
          show: true,
          errorMessage: 'Phone number(s) not available',
          errorData: notAvailableNumbers,
        },
      }));
      return;
    }
    onUpload([...modifiedNamingData, ...modifiedAssigningData]);
  };

  return (
    <>
      <Paper
        className='mr-10 ml-10 p-6 mt-5 d-flex f-justify-between'
        elevation={paperElevation}
      >
        <div className='w-75'>
          <Typography
            component='div'
            variant='body1'
            className='text-bold mb-4'
          >
            Instructions
          </Typography>
          <Typography component='div' variant='body2' className='mb-1'>
            1. Download the Excel file.
          </Typography>
          {/* <div className='mb-2 ml-2 d-flex f-align-center'>
            <WarningIcon fontSize='small' color='error' />
            <Typography variant='caption' className='color-grey'>
              To switch a number back to a company phone number, leave the
              ExtensionNumber and ExtensionId blank.
            </Typography>
          </div> */}
          <Typography component='div' variant='body2' className='mb-2'>
            2. Follow the instructions on Tab 1 to update the phone number name
            (Tab 2) and assign phone numbers (Tab 3).
          </Typography>
          <Typography component='div' variant='body2' className='mb-2'>
            3. Save and Upload the updated Excel file.
          </Typography>
          <Typography component='div' variant='body2' className='mb-2'>
            4. Verify the newly updated data is correct in the table below.
          </Typography>
          <Typography component='div' variant='body2' className='mb-2'>
            5. Click Sync Changes to update the extensions.
          </Typography>
        </div>
        <div className='w-25 d-flex f-justify-end'>
          <div>
            <Button
              variant='outlined'
              color='primary'
              startIcon={<BackupIcon />}
              disabled={isSyncing}
              onClick={() => {
                setState(prevState => ({
                  ...prevState,
                  showUploadConfirm: true,
                }));
              }}
            >
              Upload File
            </Button>
          </div>
        </div>
        <input
          id='phone-number-file'
          type='file'
          className='d-none'
          accept={`.xls, .xlsx`}
          onChange={handleFileChanges}
          onClick={evt => (evt.currentTarget.value = '')}
        />
      </Paper>
      {state.showUploadConfirm && (
        <ActionDialog
          classes={{
            confirm: clsx('color-white ml-2', {
              'bg-primary': !!state.selectedFile,
            }),
          }}
          open={state.showUploadConfirm}
          content={
            <div>
              <Typography variant='h6' className='text-bold'>
                Upload File
              </Typography>
              <Typography
                component='div'
                variant='caption'
                className='color-grey'
              >
                Please select the file to upload.
              </Typography>
              <div className='d-flex f-align-center'>
                <Button
                  size='small'
                  variant='contained'
                  color='primary'
                  className='mt-4'
                  component='label'
                  htmlFor='phone-number-file'
                >
                  Choose File
                </Button>
                {state.selectedFile && (
                  <Typography noWrap variant='caption' className='ml-2 mt-4'>
                    {state.selectedFile?.name}
                  </Typography>
                )}
              </div>
            </div>
          }
          isConfirmation
          isConfirmDisabled={!state.selectedFile}
          positiveActionLabel='Upload'
          negativeActionLabel='Cancel'
          onConfirm={() => {
            handleUpload(state.selectedFile);
            setState(prevState => ({
              ...prevState,
              showUploadConfirm: false,
              selectedFile: null,
            }));
          }}
          onCancel={() => {
            setState(prevState => ({
              ...prevState,
              showUploadConfirm: false,
              selectedFile: null,
            }));
          }}
        />
      )}
      {state.errorModal?.show && (
        <ErrorModal
          showModel={state.errorModal?.show}
          heading='Error'
          errorKey='Phone Number'
          actionLabel='OK'
          headers={state.errorModal?.errorBody && []}
          errorData={state.errorModal?.errorData}
          errorBody={state.errorModal?.errorBody}
          onClose={() => {
            setState(prevState => ({
              ...prevState,
              errorModal: {},
              selectedFile: null,
            }));
          }}
          title='Oops! Encountered an error. Please read the message below for details.'
          errorMessage={state.errorModal.errorMessage}
          gridHeaderClass={classes.errorHeaderClass}
        />
      )}
    </>
  );
};

export default UploadSection;
